import React from 'react';
import { useSelector } from 'react-redux';

import TotalTransaction from 'assets/DashboardIcon/TotalTransaction.svg';
import TransactionComplete from 'assets/DashboardIcon/TransactionComplete.svg';
import TransactionReject from 'assets/DashboardIcon/TransactionReject.svg';
import TransactionPaid from 'assets/DashboardIcon/TransactionPaid.svg';
import TransactionProcessing from 'assets/DashboardIcon/TransactionProcessing.svg';
import TransactionPending from 'assets/DashboardIcon/TransactionPending.svg';
import TransactionCreated from 'assets/DashboardIcon/TransactionCreated.svg';

import { Paper, Typography, styled, Grid, Box, CircularProgress } from '@mui/material';
import TransactionStatusCard from './TransactionStatusCard';

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: '#0E1223',
  marginBottom: '16px',
}));

const TotalCountContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
  padding: '16px',
  borderBottom: '1px solid #E0E2E7',
}));

const TotalCountIcon = styled(Box)(({ theme }) => ({
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
}));

const TotalCountText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: '#425166',
  marginRight: 'auto',
}));

const TotalCountValue = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 700,
  color: '#19273C',
}));

export default function TransactionStat() {
  const { isLoading, data, error } = useSelector((state) => state.dashboard.transactionSummary);

  const transactionData = data?.data?.data || null;

  const statusCards = [
    {
      icon: TransactionPending,
      iconColor: '#FFC14A',
      status: 'Pending',
      count: transactionData?.reviewPendingCount || 0,
    },
    {
      icon: TransactionCreated,
      iconColor: '#4EBAEA',
      status: 'Created',
      count: transactionData?.createdTransactionsCount || 0,
    },
    {
      icon: TransactionProcessing,
      iconColor: '#3994FF',
      status: 'Processing',
      count: transactionData?.processingTransactionsCount || 0,
    },
    {
      icon: TransactionComplete,
      iconColor: '#23C965',
      status: 'Completed',
      count: transactionData?.completedTransactionsCount || 0,
    },
    {
      icon: TransactionReject,
      iconColor: '#FF554A',
      status: 'Rejected',
      count: transactionData?.rejectedTransactionsCount || 0,
    },
    {
      icon: TransactionPaid,
      iconColor: '#23C965',
      status: 'Paid',
      count: transactionData?.paidTransactionsCount || 0,
    },
  ];

  return (
    <Paper sx={{ p: '20px', borderRadius: '16px', height: '100%' }}>
      <Title>Transaction Details</Title>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <>
          <TotalCountContainer>
            <TotalCountIcon>
              <img src={TotalTransaction} alt="Total Transaction" />
            </TotalCountIcon>
            <TotalCountText>Total Transaction Count</TotalCountText>
            <TotalCountValue>
              {transactionData?.totalTransactionsCount?.toLocaleString() || '0'}
            </TotalCountValue>
          </TotalCountContainer>

          <Grid container spacing={1.5}>
            {statusCards.map((card, index) => (
              <Grid item xs={6} sm={4} md={4} lg={4} key={index}>
                <TransactionStatusCard
                  icon={card.icon}
                  iconColor={card.iconColor}
                  status={card.status}
                  count={card.count}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Paper>
  );
}
